import {
  CalendarAddAlt,
  CheckmarkOutline,
  DeliveryParcel,
  Information,
  SearchLocate,
} from "@carbon/icons-react";
import { Edit } from "@mui/icons-material";
import { Button, List, ListItem, ListItemText } from "@mui/material";
import moment from "moment";
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useOrderContext } from "../../../../contexts/OrderContext";
import { useProgression } from "../../../../hooks/useProgression";
import EditInformation from "../../../newOrder/components/editInformation/EditInformation";
import "./OutboundSummary.scss";

interface OutboundSummaryProps {
  isConfirmation: boolean;
}

const OutboundSummary = (props: OutboundSummaryProps) => {
  const { t } = useTranslation();
  const { savedProgress } = useOrderContext();
  const { jumpTo } = useProgression();

  const { isConfirmation } = props;
  const {
    outboundGeneralInfo,
    deliveryItems,
    goodsIssuePlanned,
    deliveryDatePlanned,
  } = savedProgress;

  const formattedGoodsIssueDateString =
    goodsIssuePlanned &&
    `${moment(new Date(goodsIssuePlanned))
      .locale(t("locale"))
      .format(t("dateTimeDayYearFormat"))} `;

  const formattedDeliveryDateString =
    deliveryDatePlanned &&
    `${moment(new Date(deliveryDatePlanned))
      .locale(t("locale"))
      .format(t("dateTimeDayYearFormat"))} `;

  const renderEditInformation = (
    icon: ReactElement,
    header: string,
    body: ReactElement,
    jumpToScreen: number,
  ) => {
    return (
      <div className="page-content-paragraph">
        <EditInformation
          icon={icon}
          text={
            <div>
              <b>{header}</b>
              {body}
            </div>
          }
          actionButton={
            isConfirmation ? undefined : (
              <Button onClick={() => jumpTo(jumpToScreen)}>
                <Edit />
              </Button>
            )
          }
        />
      </div>
    );
  };

  const renderCheckDataSection = () => {
    return isConfirmation ? (
      <div className="summary-check-data mb-5 mt-18">
        <CheckmarkOutline
          className="summary-check-data-icon-success"
          data-testid="summary-check-data-icon-success"
          size={32}
        />
        <div className="summary-check-data-text">
          {t("outboundDeliveryCreationSuccess")}
        </div>
      </div>
    ) : (
      <div className="summary-check-data mb-5">
        <div className="summary-check-data-icon">
          <SearchLocate size={24} />
        </div>
        <div className="summary-check-data-text">{t("checkData")}</div>
      </div>
    );
  };

  return (
    <div data-testid="outboundSummaryContent" className="page-content">
      <div>
        {renderCheckDataSection()}
        <div className="page-content-divider" />
        {renderEditInformation(
          <Information size={24} />,
          `${t("enterInfo")}`,
          <div>
            {`${outboundGeneralInfo?.deliveryNumber} | ${outboundGeneralInfo?.location} | ${outboundGeneralInfo?.recipientCompanyName || outboundGeneralInfo?.recipient}`}
          </div>,
          1,
        )}
        <div className="page-content-divider" />
        {renderEditInformation(
          <DeliveryParcel size={24} />,
          `${t("enterItems")} - ${deliveryItems?.length} ${deliveryItems && deliveryItems?.length > 1 ? t("items") : t("item")}`,
          <List>
            {deliveryItems?.map((item) => (
              <ListItem key={item.id} className="summary-list-item">
                <ListItemText
                  primary={`${item.material} - ${item.stockType} `}
                  secondary={`${t("quantity")}: ${item.quantity} ${item.unitOfMeasurement}`}
                />
              </ListItem>
            ))}
          </List>,
          2,
        )}
        <div className="page-content-divider" />
        {renderEditInformation(
          <CalendarAddAlt size={24} />,
          `${t("enterTimes")}`,
          <div>
            {`${t("goodsIssuePlanned")}: ${formattedGoodsIssueDateString}`}
            {deliveryDatePlanned &&
              ` | ${t("deliveryDatePlanned")}: ${formattedDeliveryDateString}`}
          </div>,
          3,
        )}
      </div>
    </div>
  );
};

export default OutboundSummary;
